import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import TextField from '@mui/material/TextField';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import UserPicker from 'components/Pickers/UserPicker';
import { SxProps, Theme } from '@mui/material/styles';
import FilePicker from 'components/Pickers/FilePicker';
import { Accept } from 'react-dropzone';
import { ESignAdhocRequestData, ESignAdhocRequestSignatory } from 'common/models/ESigning';
import { GenerateAdhocEsignRequest } from 'services/ESignService';
import InfoButtonToolTip from 'components/InfoButtonToolTip';
import Typography from '@mui/material/Typography';
import ContactPicker from 'components/Pickers/ContactPicker';
import CandidatePicker from 'components/Pickers/CandidatePicker';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import DialogContentText from '@mui/material/DialogContentText';
import Chip from '@mui/material/Chip';
import { Contact } from 'common/models/Contacts';
import { CandidateList } from 'common/models/Candidates/CandidateList';

interface Props {
  open: boolean;
  entityTypeId?: number;
  recordId?: number;
  defaultSignatory1Id?: number;
  defaultSignatory1Email?: string;
  closeHandler: () => void;
}

const infoContents: React.ReactElement = (
  <>
    <Typography variant="body2">Send a document for electronic signing.</Typography>
    <br />
    <Typography variant="subtitle2">Step 1 (Setup):</Typography>
    <Typography variant="body2">
      Set the desired document title, the number of signatories required and whether or not the
      signing must be done in order.
    </Typography>
    <br />
    <Typography variant="subtitle2">Step 2 (Signatories):</Typography>
    <Typography variant="body2">Set the signator entities and their order.</Typography>
    <Typography component="div" variant="body2">
      <Chip
        label="[sign|signer#]"
        size="small"
        sx={{ color: 'inherit', bgcolor: (t) => t.palette.grey[600] }}
      />
      : Use this button to copy the signer placeholder to the clipboard.
    </Typography>
    <Typography component="div" variant="body2">
      <Chip
        label="[date|signer#]"
        size="small"
        sx={{ color: 'inherit', bgcolor: (t) => t.palette.grey[600] }}
      />
      : Use this button to copy the dating placeholder to the clipboard.
    </Typography>
    <br />
    <Typography variant="subtitle2">Note:</Typography>
    <Typography variant="body2">
      Put the copied placeholders in the document you want to send. This way you can set where each
      signatory will sign / date.
    </Typography>
    <br />
    <Typography variant="subtitle2">Step 3 (Document):</Typography>
    <Typography variant="body2">
      Select the document to upload, only files of type pdf or docx are allowed.
    </Typography>
  </>
);

const signatoryTypes: React.ReactElement[] = [
  <MenuItem key="0" value="0">
    None
  </MenuItem>,
  <MenuItem key="3" value="3">
    Candidate
  </MenuItem>,
  <MenuItem key="2" value="2">
    Contact
  </MenuItem>,
  <MenuItem key="7" value="7">
    User
  </MenuItem>,
];

const contactSignatorType = 2;
const candidateSignatorType = 3;
const userSignatorType = 7;

const signatoryElementStyle: SxProps<Theme> = { flex: '1 1 0' };
const chipStyle: SxProps<Theme> = { minWidth: '105px' };

const filesFilter: Accept = {
  // "application/msword": ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/pdf': ['.pdf'],
};

const contactEmailValidationMessage = (c: Contact | null) => {
  if (c && (!c.email || !c.email.trim())) return 'No Email';
  return '';
};

const candidateEmailValidationMessage = (c: CandidateList | null) => {
  if (c && (!c.emailAddress || !c.emailAddress.trim())) return 'No Email';
  return '';
};

export default function SendAdhocDocumentForESign({
  open,
  entityTypeId = 0,
  recordId = 0,
  defaultSignatory1Id = 0,
  defaultSignatory1Email = '',
  closeHandler,
}: Props) {
  const [isLoading, setIsLoading] = useState(false);
  const [infoMessage, setInfoMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const [activeStep, setActiveStep] = useState(0);
  const [validateSetupStep, setValidateSetupStep] = useState(false);
  const [validateSignatorsStep, setValidateSignatorsStep] = useState(false);

  const [documentTitle, setDocumentTitle] = useState('');
  const [isOrderedSigners, setIsOrderedSigners] = useState(true);
  const [numberOfSignatories, setNumberOfSignatories] = useState(1);
  const [signatoryEntityType1, setSignatoryEntityType1] = useState(0);
  const [signatoryEntityType2, setSignatoryEntityType2] = useState(0);
  const [signatoryEntityType3, setSignatoryEntityType3] = useState(0);
  const [signatoryEntityType4, setSignatoryEntityType4] = useState(0);
  const [signatoryEntityType5, setSignatoryEntityType5] = useState(0);
  const [signatoryEntityType6, setSignatoryEntityType6] = useState(0);
  const [signatoryEntityType7, setSignatoryEntityType7] = useState(0);
  const [signatoryEntityType8, setSignatoryEntityType8] = useState(0);
  const [signatoryEntityType9, setSignatoryEntityType9] = useState(0);
  const [signatoryEntityType10, setSignatoryEntityType10] = useState(0);
  const [signatoryPlayerId1, setSignatoryPlayerId1] = useState(0);
  const [signatoryPlayerId2, setSignatoryPlayerId2] = useState(0);
  const [signatoryPlayerId3, setSignatoryPlayerId3] = useState(0);
  const [signatoryPlayerId4, setSignatoryPlayerId4] = useState(0);
  const [signatoryPlayerId5, setSignatoryPlayerId5] = useState(0);
  const [signatoryPlayerId6, setSignatoryPlayerId6] = useState(0);
  const [signatoryPlayerId7, setSignatoryPlayerId7] = useState(0);
  const [signatoryPlayerId8, setSignatoryPlayerId8] = useState(0);
  const [signatoryPlayerId9, setSignatoryPlayerId9] = useState(0);
  const [signatoryPlayerId10, setSignatoryPlayerId10] = useState(0);
  const [signatory1ErrorMessage, setSignatory1ErrorMessage] = useState('');
  const [signatory2ErrorMessage, setSignatory2ErrorMessage] = useState('');
  const [signatory3ErrorMessage, setSignatory3ErrorMessage] = useState('');
  const [signatory4ErrorMessage, setSignatory4ErrorMessage] = useState('');
  const [signatory5ErrorMessage, setSignatory5ErrorMessage] = useState('');
  const [signatory6ErrorMessage, setSignatory6ErrorMessage] = useState('');
  const [signatory7ErrorMessage, setSignatory7ErrorMessage] = useState('');
  const [signatory8ErrorMessage, setSignatory8ErrorMessage] = useState('');
  const [signatory9ErrorMessage, setSignatory9ErrorMessage] = useState('');
  const [signatory10ErrorMessage, setSignatory10ErrorMessage] = useState('');
  const [filesToUpload, setFilesToUpload] = useState<File[]>([]);

  const defaultSignatory1ErrorMessage = useMemo(() => {
    return !defaultSignatory1Email || !defaultSignatory1Email.trim() ? 'No Email' : '';
  }, [defaultSignatory1Email]);

  useEffect(() => {
    if (!open) {
      setIsLoading(false);
      setInfoMessage('');
      setSuccessMessage('');
      setErrorMessage('');

      setActiveStep(0);
      setValidateSetupStep(false);
      setValidateSignatorsStep(false);
      setDocumentTitle('');
      setIsOrderedSigners(true);
      setNumberOfSignatories(1);

      if (entityTypeId === 1) setSignatoryEntityType1(2);
      else if (entityTypeId === 2) setSignatoryEntityType1(2);
      else if (entityTypeId === 3) setSignatoryEntityType1(3);
      else if (entityTypeId === 4) setSignatoryEntityType1(2);
      else if (entityTypeId === 5) setSignatoryEntityType1(2);
      else if (entityTypeId === 8) setSignatoryEntityType1(2);
      else setSignatoryEntityType1(0);

      setSignatoryEntityType2(0);
      setSignatoryEntityType3(0);
      setSignatoryEntityType4(0);
      setSignatoryEntityType5(0);
      setSignatoryEntityType6(0);
      setSignatoryEntityType7(0);
      setSignatoryEntityType8(0);
      setSignatoryEntityType9(0);
      setSignatoryEntityType10(0);

      setSignatoryPlayerId1(defaultSignatory1Id);
      setSignatoryPlayerId2(0);
      setSignatoryPlayerId3(0);
      setSignatoryPlayerId4(0);
      setSignatoryPlayerId5(0);
      setSignatoryPlayerId6(0);
      setSignatoryPlayerId7(0);
      setSignatoryPlayerId8(0);
      setSignatoryPlayerId9(0);
      setSignatoryPlayerId10(0);

      setSignatory1ErrorMessage(defaultSignatory1ErrorMessage);
      setSignatory2ErrorMessage('');
      setSignatory3ErrorMessage('');
      setSignatory4ErrorMessage('');
      setSignatory5ErrorMessage('');
      setSignatory6ErrorMessage('');
      setSignatory7ErrorMessage('');
      setSignatory8ErrorMessage('');
      setSignatory9ErrorMessage('');
      setSignatory10ErrorMessage('');

      setFilesToUpload([]);
    }
  }, [defaultSignatory1ErrorMessage, defaultSignatory1Id, entityTypeId, open]);

  const onSubmitCallback = useCallback(async () => {
    setIsLoading(true);
    const signatories: ESignAdhocRequestSignatory[] = [
      { entityId: signatoryEntityType1, playerId: signatoryPlayerId1 },
    ];
    if (numberOfSignatories >= 2)
      signatories.push({ entityId: signatoryEntityType2, playerId: signatoryPlayerId2 });
    if (numberOfSignatories >= 3)
      signatories.push({ entityId: signatoryEntityType3, playerId: signatoryPlayerId3 });
    if (numberOfSignatories >= 4)
      signatories.push({ entityId: signatoryEntityType4, playerId: signatoryPlayerId4 });
    if (numberOfSignatories >= 5)
      signatories.push({ entityId: signatoryEntityType5, playerId: signatoryPlayerId5 });
    if (numberOfSignatories >= 6)
      signatories.push({ entityId: signatoryEntityType6, playerId: signatoryPlayerId6 });
    if (numberOfSignatories >= 7)
      signatories.push({ entityId: signatoryEntityType7, playerId: signatoryPlayerId7 });
    if (numberOfSignatories >= 8)
      signatories.push({ entityId: signatoryEntityType8, playerId: signatoryPlayerId8 });
    if (numberOfSignatories >= 9)
      signatories.push({ entityId: signatoryEntityType9, playerId: signatoryPlayerId9 });
    if (numberOfSignatories >= 10)
      signatories.push({ entityId: signatoryEntityType10, playerId: signatoryPlayerId10 });

    const data: ESignAdhocRequestData = {
      documentTitle: documentTitle,
      entityId: entityTypeId,
      playerId: recordId,
      isOrderedSigners: isOrderedSigners,
      signatories: signatories,
    };

    const file = filesToUpload[0];

    const isTest = false;
    const res = await GenerateAdhocEsignRequest(data, file, isTest, setErrorMessage);
    if (res) {
      setSuccessMessage('ESign Request Sent');
      closeHandler();
    }

    setIsLoading(false);
  }, [
    closeHandler,
    documentTitle,
    entityTypeId,
    filesToUpload,
    isOrderedSigners,
    numberOfSignatories,
    recordId,
    signatoryEntityType1,
    signatoryEntityType10,
    signatoryEntityType2,
    signatoryEntityType3,
    signatoryEntityType4,
    signatoryEntityType5,
    signatoryEntityType6,
    signatoryEntityType7,
    signatoryEntityType8,
    signatoryEntityType9,
    signatoryPlayerId1,
    signatoryPlayerId10,
    signatoryPlayerId2,
    signatoryPlayerId3,
    signatoryPlayerId4,
    signatoryPlayerId5,
    signatoryPlayerId6,
    signatoryPlayerId7,
    signatoryPlayerId8,
    signatoryPlayerId9,
  ]);

  const isActiveStepLast = useMemo(() => activeStep === 2, [activeStep]);

  const isSetupStepError = useMemo(() => {
    return !documentTitle.trim();
  }, [documentTitle]);

  const isSignatoryStepError = useMemo(() => {
    if (signatoryEntityType1 === 0 || signatoryPlayerId1 === 0 || Boolean(signatory1ErrorMessage))
      return true;
    if (
      numberOfSignatories >= 2 &&
      (signatoryEntityType2 === 0 || signatoryPlayerId2 === 0 || Boolean(signatory2ErrorMessage))
    )
      return true;
    if (
      numberOfSignatories >= 3 &&
      (signatoryEntityType3 === 0 || signatoryPlayerId3 === 0 || Boolean(signatory3ErrorMessage))
    )
      return true;
    if (
      numberOfSignatories >= 4 &&
      (signatoryEntityType4 === 0 || signatoryPlayerId4 === 0 || Boolean(signatory4ErrorMessage))
    )
      return true;
    if (
      numberOfSignatories >= 5 &&
      (signatoryEntityType5 === 0 || signatoryPlayerId5 === 0 || Boolean(signatory5ErrorMessage))
    )
      return true;
    if (
      numberOfSignatories >= 6 &&
      (signatoryEntityType6 === 0 || signatoryPlayerId6 === 0 || Boolean(signatory6ErrorMessage))
    )
      return true;
    if (
      numberOfSignatories >= 7 &&
      (signatoryEntityType7 === 0 || signatoryPlayerId7 === 0 || Boolean(signatory7ErrorMessage))
    )
      return true;
    if (
      numberOfSignatories >= 8 &&
      (signatoryEntityType8 === 0 || signatoryPlayerId8 === 0 || Boolean(signatory8ErrorMessage))
    )
      return true;
    if (
      numberOfSignatories >= 9 &&
      (signatoryEntityType9 === 0 || signatoryPlayerId9 === 0 || Boolean(signatory9ErrorMessage))
    )
      return true;
    if (
      numberOfSignatories >= 10 &&
      (signatoryEntityType10 === 0 || signatoryPlayerId10 === 0 || Boolean(signatory10ErrorMessage))
    )
      return true;
    return false;
  }, [
    numberOfSignatories,
    signatory10ErrorMessage,
    signatory1ErrorMessage,
    signatory2ErrorMessage,
    signatory3ErrorMessage,
    signatory4ErrorMessage,
    signatory5ErrorMessage,
    signatory6ErrorMessage,
    signatory7ErrorMessage,
    signatory8ErrorMessage,
    signatory9ErrorMessage,
    signatoryEntityType1,
    signatoryEntityType10,
    signatoryEntityType2,
    signatoryEntityType3,
    signatoryEntityType4,
    signatoryEntityType5,
    signatoryEntityType6,
    signatoryEntityType7,
    signatoryEntityType8,
    signatoryEntityType9,
    signatoryPlayerId1,
    signatoryPlayerId10,
    signatoryPlayerId2,
    signatoryPlayerId3,
    signatoryPlayerId4,
    signatoryPlayerId5,
    signatoryPlayerId6,
    signatoryPlayerId7,
    signatoryPlayerId8,
    signatoryPlayerId9,
  ]);

  const nextStepHandler = useCallback(() => {
    let canContinue = true;
    if (activeStep === 0) {
      setValidateSetupStep(true);
      canContinue = !isSetupStepError;
    } else if (activeStep === 1) {
      setValidateSignatorsStep(true);
      canContinue = !isSignatoryStepError;
    }
    if (canContinue) setActiveStep((prev) => prev + 1);
  }, [activeStep, isSetupStepError, isSignatoryStepError]);

  const prevStepHandler = useCallback(() => {
    setActiveStep((prev) => prev - 1);
  }, []);

  const addFilesCallback = useCallback((files: File[]) => {
    setFilesToUpload((prev) => [...prev, ...files]);
  }, []);

  const removeFileCallback = useCallback((index: number) => {
    setFilesToUpload((prev) => {
      const files = [...prev];
      files.splice(index, 1);
      return files;
    });
  }, []);

  const onContactSignatory1Change = useCallback((c: Contact | null) => {
    setSignatoryPlayerId1(c ? c.id : 0);
    setSignatory1ErrorMessage(contactEmailValidationMessage(c));
  }, []);

  const onContactSignatory2Change = useCallback((c: Contact | null) => {
    setSignatoryPlayerId2(c ? c.id : 0);
    setSignatory2ErrorMessage(contactEmailValidationMessage(c));
  }, []);

  const onContactSignatory3Change = useCallback((c: Contact | null) => {
    setSignatoryPlayerId3(c ? c.id : 0);
    setSignatory3ErrorMessage(contactEmailValidationMessage(c));
  }, []);

  const onContactSignatory4Change = useCallback((c: Contact | null) => {
    setSignatoryPlayerId4(c ? c.id : 0);
    setSignatory4ErrorMessage(contactEmailValidationMessage(c));
  }, []);

  const onContactSignatory5Change = useCallback((c: Contact | null) => {
    setSignatoryPlayerId5(c ? c.id : 0);
    setSignatory5ErrorMessage(contactEmailValidationMessage(c));
  }, []);

  const onContactSignatory6Change = useCallback((c: Contact | null) => {
    setSignatoryPlayerId6(c ? c.id : 0);
    setSignatory6ErrorMessage(contactEmailValidationMessage(c));
  }, []);

  const onContactSignatory7Change = useCallback((c: Contact | null) => {
    setSignatoryPlayerId7(c ? c.id : 0);
    setSignatory7ErrorMessage(contactEmailValidationMessage(c));
  }, []);

  const onContactSignatory8Change = useCallback((c: Contact | null) => {
    setSignatoryPlayerId8(c ? c.id : 0);
    setSignatory8ErrorMessage(contactEmailValidationMessage(c));
  }, []);

  const onContactSignatory9Change = useCallback((c: Contact | null) => {
    setSignatoryPlayerId9(c ? c.id : 0);
    setSignatory9ErrorMessage(contactEmailValidationMessage(c));
  }, []);

  const onContactSignatory10Change = useCallback((c: Contact | null) => {
    setSignatoryPlayerId10(c ? c.id : 0);
    setSignatory10ErrorMessage(contactEmailValidationMessage(c));
  }, []);

  const onCandidateSignatory1Change = useCallback((c: CandidateList | null) => {
    setSignatoryPlayerId1(c ? c.id : 0);
    setSignatory1ErrorMessage(candidateEmailValidationMessage(c));
  }, []);

  const onCandidateSignatory2Change = useCallback((c: CandidateList | null) => {
    setSignatoryPlayerId2(c ? c.id : 0);
    setSignatory2ErrorMessage(candidateEmailValidationMessage(c));
  }, []);

  const onCandidateSignatory3Change = useCallback((c: CandidateList | null) => {
    setSignatoryPlayerId3(c ? c.id : 0);
    setSignatory3ErrorMessage(candidateEmailValidationMessage(c));
  }, []);

  const onCandidateSignatory4Change = useCallback((c: CandidateList | null) => {
    setSignatoryPlayerId4(c ? c.id : 0);
    setSignatory4ErrorMessage(candidateEmailValidationMessage(c));
  }, []);

  const onCandidateSignatory5Change = useCallback((c: CandidateList | null) => {
    setSignatoryPlayerId5(c ? c.id : 0);
    setSignatory5ErrorMessage(candidateEmailValidationMessage(c));
  }, []);

  const onCandidateSignatory6Change = useCallback((c: CandidateList | null) => {
    setSignatoryPlayerId6(c ? c.id : 0);
    setSignatory6ErrorMessage(candidateEmailValidationMessage(c));
  }, []);

  const onCandidateSignatory7Change = useCallback((c: CandidateList | null) => {
    setSignatoryPlayerId7(c ? c.id : 0);
    setSignatory7ErrorMessage(candidateEmailValidationMessage(c));
  }, []);

  const onCandidateSignatory8Change = useCallback((c: CandidateList | null) => {
    setSignatoryPlayerId8(c ? c.id : 0);
    setSignatory8ErrorMessage(candidateEmailValidationMessage(c));
  }, []);

  const onCandidateSignatory9Change = useCallback((c: CandidateList | null) => {
    setSignatoryPlayerId9(c ? c.id : 0);
    setSignatory9ErrorMessage(candidateEmailValidationMessage(c));
  }, []);

  const onCandidateSignatory10Change = useCallback((c: CandidateList | null) => {
    setSignatoryPlayerId10(c ? c.id : 0);
    setSignatory10ErrorMessage(candidateEmailValidationMessage(c));
  }, []);

  const signatoryEntityTypeChangeCallback = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value, name } = e.target;
      if (name === 'signatoryEntityType1') {
        setSignatoryPlayerId1(0);
        setSignatoryEntityType1(+value);
      } else if (name === 'signatoryEntityType2') {
        setSignatoryPlayerId2(0);
        setSignatoryEntityType2(+value);
      } else if (name === 'signatoryEntityType3') {
        setSignatoryPlayerId3(0);
        setSignatoryEntityType3(+value);
      } else if (name === 'signatoryEntityType4') {
        setSignatoryPlayerId4(0);
        setSignatoryEntityType4(+value);
      } else if (name === 'signatoryEntityType5') {
        setSignatoryPlayerId5(0);
        setSignatoryEntityType5(+value);
      } else if (name === 'signatoryEntityType6') {
        setSignatoryPlayerId6(0);
        setSignatoryEntityType6(+value);
      } else if (name === 'signatoryEntityType7') {
        setSignatoryPlayerId7(0);
        setSignatoryEntityType7(+value);
      } else if (name === 'signatoryEntityType8') {
        setSignatoryPlayerId8(0);
        setSignatoryEntityType8(+value);
      } else if (name === 'signatoryEntityType9') {
        setSignatoryPlayerId9(0);
        setSignatoryEntityType9(+value);
      } else if (name === 'signatoryEntityType10') {
        setSignatoryPlayerId10(0);
        setSignatoryEntityType10(+value);
      }
    },
    []
  );

  const copySignPlaceholder = useCallback((e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const n = e.currentTarget.dataset.number;
    if (n) {
      navigator.clipboard.writeText(`[sign|signer${n}]`);
      setInfoMessage(`Signing placeholder for signatory ${n} copied to clipboard`);
    }
  }, []);

  const copyDatePlaceholder = useCallback((e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const n = e.currentTarget.dataset.number;
    if (n) {
      navigator.clipboard.writeText(`[date|signer${n}]`);
      setInfoMessage(`Signing placeholder for signatory ${n} copied to clipboard`);
    }
  }, []);

  return (
    <>
      <Backdrop open={isLoading} sx={{ zIndex: (t) => t.zIndex.modal + 1 }}>
        <CircularProgress sx={{ color: 'text.disabled' }} />
      </Backdrop>
      <Snackbar
        open={successMessage !== ''}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage('')}
      >
        <Alert onClose={() => setSuccessMessage('')}>{successMessage}</Alert>
      </Snackbar>
      <Snackbar
        open={infoMessage !== ''}
        autoHideDuration={3000}
        onClose={() => setInfoMessage('')}
      >
        <Alert severity="info" onClose={() => setInfoMessage('')}>
          {infoMessage}
        </Alert>
      </Snackbar>
      <Snackbar open={errorMessage !== ''} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Alert>
      </Snackbar>
      <Dialog open={open} disablePortal fullWidth maxWidth="md">
        <DialogTitle display="flex">
          <Box flexGrow={1}>Send Adhoc Document for ESigning</Box>
          <InfoButtonToolTip placement="right-start" text={infoContents} />
        </DialogTitle>
        <Stepper nonLinear activeStep={activeStep} sx={{ px: 2 }}>
          <Step>
            <StepLabel error={validateSetupStep && isSetupStepError}>Setup</StepLabel>
          </Step>
          <Step>
            <StepLabel error={validateSignatorsStep && isSignatoryStepError}>Signatories</StepLabel>
          </Step>
          <Step>
            <StepLabel>Document</StepLabel>
          </Step>
        </Stepper>
        <DialogContent>
          <Stack spacing={2} sx={{ display: activeStep === 0 ? undefined : 'none' }}>
            <TextField
              label="Document Title"
              value={documentTitle}
              error={validateSetupStep && isSetupStepError}
              onChange={({ target }) => setDocumentTitle(target.value)}
            />
            <TextField
              select
              label="Ordered Signers"
              value={isOrderedSigners ? '1' : '0'}
              onChange={({ target }) => setIsOrderedSigners(target.value === '1')}
            >
              <MenuItem value="1">Yes</MenuItem>
              <MenuItem value="0">No</MenuItem>
            </TextField>
            <TextField
              select
              label="Number of Signatories"
              value={numberOfSignatories.toString()}
              onChange={({ target }) => setNumberOfSignatories(+target.value)}
            >
              <MenuItem value="1">1</MenuItem>
              <MenuItem value="2">2</MenuItem>
              <MenuItem value="3">3</MenuItem>
              <MenuItem value="4">4</MenuItem>
              <MenuItem value="5">5</MenuItem>
              <MenuItem value="6">6</MenuItem>
              <MenuItem value="7">7</MenuItem>
              <MenuItem value="8">8</MenuItem>
              <MenuItem value="9">9</MenuItem>
              <MenuItem value="10">10</MenuItem>
            </TextField>
          </Stack>
          <Stack spacing={2} sx={{ display: activeStep === 1 ? undefined : 'none' }}>
            <DialogContentText>
              Use the signer & date tags for each signatory to include in your document where they
              are required
            </DialogContentText>
            <Box display={numberOfSignatories >= 1 ? 'flex' : 'none'} gap={1}>
              <TextField
                select
                label="Signatory 1 Type"
                value={signatoryEntityType1.toString()}
                name="signatoryEntityType1"
                onChange={signatoryEntityTypeChangeCallback}
                sx={signatoryElementStyle}
                error={validateSignatorsStep && signatoryEntityType1 === 0}
              >
                {signatoryTypes}
              </TextField>
              {signatoryEntityType1 === userSignatorType && (
                <UserPicker
                  label="Signatory 1 User"
                  userId={signatoryPlayerId1}
                  onSelect={(u) => setSignatoryPlayerId1(u ? u.id : 0)}
                  error={validateSignatorsStep && signatoryPlayerId1 === 0}
                  sx={signatoryElementStyle}
                />
              )}
              {signatoryEntityType1 === contactSignatorType && (
                <ContactPicker
                  label="Signatory 1 Contact"
                  value={signatoryPlayerId1}
                  onSelectCallback={onContactSignatory1Change}
                  sx={signatoryElementStyle}
                  error={
                    validateSignatorsStep &&
                    (signatoryPlayerId1 === 0 || Boolean(signatory1ErrorMessage))
                  }
                  disableQuickCreate
                  errorMessage={signatory1ErrorMessage}
                />
              )}
              {signatoryEntityType1 === candidateSignatorType && (
                <CandidatePicker
                  label="Signatory 1 Candidate"
                  value={signatoryPlayerId1}
                  onSelectCallback={onCandidateSignatory1Change}
                  sx={signatoryElementStyle}
                  error={
                    validateSignatorsStep &&
                    (signatoryPlayerId1 === 0 || Boolean(signatory1ErrorMessage))
                  }
                  helperText={signatory1ErrorMessage}
                />
              )}
              <Box display="flex" gap={1} alignItems="center">
                <Chip
                  label="[sign|signer1]"
                  size="small"
                  clickable
                  data-number="1"
                  onClick={copySignPlaceholder}
                  title="Copy to clipboard"
                  sx={chipStyle}
                />
                <Chip
                  label="[date|signer1]"
                  size="small"
                  clickable
                  data-number="1"
                  onClick={copyDatePlaceholder}
                  title="Copy to clipboard"
                  sx={chipStyle}
                />
              </Box>
            </Box>
            <Box display={numberOfSignatories >= 2 ? 'flex' : 'none'} gap={1}>
              <TextField
                select
                label="Signatory 2 Type"
                value={signatoryEntityType2.toString()}
                name="signatoryEntityType2"
                onChange={signatoryEntityTypeChangeCallback}
                sx={signatoryElementStyle}
                error={validateSignatorsStep && signatoryEntityType2 === 0}
              >
                {signatoryTypes}
              </TextField>
              {signatoryEntityType2 === userSignatorType && (
                <UserPicker
                  label="Signatory 2 User"
                  userId={signatoryPlayerId2}
                  onSelect={(u) => setSignatoryPlayerId2(u ? u.id : 0)}
                  sx={signatoryElementStyle}
                  error={validateSignatorsStep && signatoryPlayerId2 === 0}
                />
              )}
              {signatoryEntityType2 === contactSignatorType && (
                <ContactPicker
                  label="Signatory 2 Contact"
                  value={signatoryPlayerId2}
                  onSelectCallback={onContactSignatory2Change}
                  sx={signatoryElementStyle}
                  error={
                    validateSignatorsStep &&
                    (signatoryPlayerId2 === 0 || Boolean(signatory2ErrorMessage))
                  }
                  disableQuickCreate
                  errorMessage={signatory2ErrorMessage}
                />
              )}
              {signatoryEntityType2 === candidateSignatorType && (
                <CandidatePicker
                  label="Signatory 2 Candidate"
                  value={signatoryPlayerId2}
                  onSelectCallback={onCandidateSignatory2Change}
                  sx={signatoryElementStyle}
                  error={
                    validateSignatorsStep &&
                    (signatoryPlayerId2 === 0 || Boolean(signatory2ErrorMessage))
                  }
                  helperText={signatory2ErrorMessage}
                />
              )}
              <Box display="flex" gap={1} alignItems="center">
                <Chip
                  label="[sign|signer2]"
                  size="small"
                  clickable
                  data-number="2"
                  onClick={copySignPlaceholder}
                  title="Copy to clipboard"
                  sx={chipStyle}
                />
                <Chip
                  label="[date|signer2]"
                  size="small"
                  clickable
                  data-number="2"
                  onClick={copyDatePlaceholder}
                  title="Copy to clipboard"
                  sx={chipStyle}
                />
              </Box>
            </Box>
            <Box display={numberOfSignatories >= 3 ? 'flex' : 'none'} gap={1}>
              <TextField
                select
                label="Signatory 3 Type"
                value={signatoryEntityType3.toString()}
                name="signatoryEntityType3"
                onChange={signatoryEntityTypeChangeCallback}
                sx={signatoryElementStyle}
                error={validateSignatorsStep && signatoryEntityType3 === 0}
              >
                {signatoryTypes}
              </TextField>
              {signatoryEntityType3 === userSignatorType && (
                <UserPicker
                  label="Signatory 3 User"
                  userId={signatoryPlayerId3}
                  onSelect={(u) => setSignatoryPlayerId3(u ? u.id : 0)}
                  sx={signatoryElementStyle}
                  error={validateSignatorsStep && signatoryPlayerId3 === 0}
                />
              )}
              {signatoryEntityType3 === contactSignatorType && (
                <ContactPicker
                  label="Signatory 3 Contact"
                  value={signatoryPlayerId3}
                  onSelectCallback={onContactSignatory3Change}
                  sx={signatoryElementStyle}
                  error={
                    validateSignatorsStep &&
                    (signatoryPlayerId3 === 0 || Boolean(signatory3ErrorMessage))
                  }
                  disableQuickCreate
                  errorMessage={signatory3ErrorMessage}
                />
              )}
              {signatoryEntityType3 === candidateSignatorType && (
                <CandidatePicker
                  label="Signatory 3 Candidate"
                  value={signatoryPlayerId3}
                  onSelectCallback={onCandidateSignatory3Change}
                  sx={signatoryElementStyle}
                  error={
                    validateSignatorsStep &&
                    (signatoryPlayerId3 === 0 || Boolean(signatory3ErrorMessage))
                  }
                  helperText={signatory3ErrorMessage}
                />
              )}
              <Box display="flex" gap={1} alignItems="center">
                <Chip
                  label="[sign|signer3]"
                  size="small"
                  clickable
                  data-number="3"
                  onClick={copySignPlaceholder}
                  title="Copy to clipboard"
                  sx={chipStyle}
                />
                <Chip
                  label="[date|signer3]"
                  size="small"
                  clickable
                  data-number="3"
                  onClick={copyDatePlaceholder}
                  title="Copy to clipboard"
                  sx={chipStyle}
                />
              </Box>
            </Box>
            <Box display={numberOfSignatories >= 4 ? 'flex' : 'none'} gap={1}>
              <TextField
                select
                label="Signatory 4 Type"
                value={signatoryEntityType4.toString()}
                name="signatoryEntityType4"
                onChange={signatoryEntityTypeChangeCallback}
                sx={signatoryElementStyle}
                error={validateSignatorsStep && signatoryEntityType4 === 0}
              >
                {signatoryTypes}
              </TextField>
              {signatoryEntityType4 === userSignatorType && (
                <UserPicker
                  label="Signatory 4 User"
                  userId={signatoryPlayerId4}
                  onSelect={(u) => setSignatoryPlayerId4(u ? u.id : 0)}
                  sx={signatoryElementStyle}
                  error={validateSignatorsStep && signatoryPlayerId4 === 0}
                />
              )}
              {signatoryEntityType4 === contactSignatorType && (
                <ContactPicker
                  label="Signatory 4 Contact"
                  value={signatoryPlayerId4}
                  onSelectCallback={onContactSignatory4Change}
                  sx={signatoryElementStyle}
                  error={
                    validateSignatorsStep &&
                    (signatoryPlayerId4 === 0 || Boolean(signatory4ErrorMessage))
                  }
                  disableQuickCreate
                  errorMessage={signatory4ErrorMessage}
                />
              )}
              {signatoryEntityType4 === candidateSignatorType && (
                <CandidatePicker
                  label="Signatory 4 Candidate"
                  value={signatoryPlayerId4}
                  onSelectCallback={onCandidateSignatory4Change}
                  sx={signatoryElementStyle}
                  error={
                    validateSignatorsStep &&
                    (signatoryPlayerId4 === 0 || Boolean(signatory4ErrorMessage))
                  }
                  helperText={signatory4ErrorMessage}
                />
              )}
              <Box display="flex" gap={1} alignItems="center">
                <Chip
                  label="[sign|signer4]"
                  size="small"
                  clickable
                  data-number="4"
                  onClick={copySignPlaceholder}
                  title="Copy to clipboard"
                  sx={chipStyle}
                />
                <Chip
                  label="[date|signer4]"
                  size="small"
                  clickable
                  data-number="4"
                  onClick={copyDatePlaceholder}
                  title="Copy to clipboard"
                  sx={chipStyle}
                />
              </Box>
            </Box>
            <Box display={numberOfSignatories >= 5 ? 'flex' : 'none'} gap={1}>
              <TextField
                select
                label="Signatory 5 Type"
                value={signatoryEntityType5.toString()}
                name="signatoryEntityType5"
                onChange={signatoryEntityTypeChangeCallback}
                sx={signatoryElementStyle}
                error={validateSignatorsStep && signatoryEntityType5 === 0}
              >
                {signatoryTypes}
              </TextField>
              {signatoryEntityType5 === userSignatorType && (
                <UserPicker
                  label="Signatory 5 User"
                  userId={signatoryPlayerId5}
                  onSelect={(u) => setSignatoryPlayerId5(u ? u.id : 0)}
                  sx={signatoryElementStyle}
                  error={validateSignatorsStep && signatoryPlayerId5 === 0}
                />
              )}
              {signatoryEntityType5 === contactSignatorType && (
                <ContactPicker
                  label="Signatory 5 Contact"
                  value={signatoryPlayerId5}
                  onSelectCallback={onContactSignatory5Change}
                  sx={signatoryElementStyle}
                  error={
                    validateSignatorsStep &&
                    (signatoryPlayerId5 === 0 || Boolean(signatory5ErrorMessage))
                  }
                  disableQuickCreate
                  errorMessage={signatory5ErrorMessage}
                />
              )}
              {signatoryEntityType5 === candidateSignatorType && (
                <CandidatePicker
                  label="Signatory 5 Candidate"
                  value={signatoryPlayerId5}
                  onSelectCallback={onCandidateSignatory5Change}
                  sx={signatoryElementStyle}
                  error={
                    validateSignatorsStep &&
                    (signatoryPlayerId5 === 0 || Boolean(signatory5ErrorMessage))
                  }
                  helperText={signatory5ErrorMessage}
                />
              )}
              <Box display="flex" gap={1} alignItems="center">
                <Chip
                  label="[sign|signer5]"
                  size="small"
                  clickable
                  data-number="5"
                  onClick={copySignPlaceholder}
                  title="Copy to clipboard"
                  sx={chipStyle}
                />
                <Chip
                  label="[date|signer5]"
                  size="small"
                  clickable
                  data-number="5"
                  onClick={copyDatePlaceholder}
                  title="Copy to clipboard"
                  sx={chipStyle}
                />
              </Box>
            </Box>
            <Box display={numberOfSignatories >= 6 ? 'flex' : 'none'} gap={1}>
              <TextField
                select
                label="Signatory 6 Type"
                value={signatoryEntityType6.toString()}
                name="signatoryEntityType6"
                onChange={signatoryEntityTypeChangeCallback}
                sx={signatoryElementStyle}
                error={validateSignatorsStep && signatoryEntityType6 === 0}
              >
                {signatoryTypes}
              </TextField>
              {signatoryEntityType6 === userSignatorType && (
                <UserPicker
                  label="Signatory 6 User"
                  userId={signatoryPlayerId6}
                  onSelect={(u) => setSignatoryPlayerId6(u ? u.id : 0)}
                  sx={signatoryElementStyle}
                  error={validateSignatorsStep && signatoryPlayerId6 === 0}
                />
              )}
              {signatoryEntityType6 === contactSignatorType && (
                <ContactPicker
                  label="Signatory 6 Contact"
                  value={signatoryPlayerId6}
                  onSelectCallback={onContactSignatory6Change}
                  sx={signatoryElementStyle}
                  error={
                    validateSignatorsStep &&
                    (signatoryPlayerId6 === 0 || Boolean(signatory6ErrorMessage))
                  }
                  disableQuickCreate
                  errorMessage={signatory6ErrorMessage}
                />
              )}
              {signatoryEntityType6 === candidateSignatorType && (
                <CandidatePicker
                  label="Signatory 6 Candidate"
                  value={signatoryPlayerId6}
                  onSelectCallback={onCandidateSignatory6Change}
                  sx={signatoryElementStyle}
                  error={
                    validateSignatorsStep &&
                    (signatoryPlayerId6 === 0 || Boolean(signatory6ErrorMessage))
                  }
                  helperText={signatory6ErrorMessage}
                />
              )}
              <Box display="flex" gap={1} alignItems="center">
                <Chip
                  label="[sign|signer6]"
                  size="small"
                  clickable
                  data-number="6"
                  onClick={copySignPlaceholder}
                  title="Copy to clipboard"
                  sx={chipStyle}
                />
                <Chip
                  label="[date|signer6]"
                  size="small"
                  clickable
                  data-number="6"
                  onClick={copyDatePlaceholder}
                  title="Copy to clipboard"
                  sx={chipStyle}
                />
              </Box>
            </Box>
            <Box display={numberOfSignatories >= 7 ? 'flex' : 'none'} gap={1}>
              <TextField
                select
                label="Signatory 7 Type"
                value={signatoryEntityType7.toString()}
                name="signatoryEntityType7"
                onChange={signatoryEntityTypeChangeCallback}
                sx={signatoryElementStyle}
                error={validateSignatorsStep && signatoryEntityType7 === 0}
              >
                {signatoryTypes}
              </TextField>
              {signatoryEntityType7 === userSignatorType && (
                <UserPicker
                  label="Signatory 7 User"
                  userId={signatoryPlayerId7}
                  onSelect={(u) => setSignatoryPlayerId7(u ? u.id : 0)}
                  sx={signatoryElementStyle}
                  error={validateSignatorsStep && signatoryPlayerId7 === 0}
                />
              )}
              {signatoryEntityType7 === contactSignatorType && (
                <ContactPicker
                  label="Signatory 7 Contact"
                  value={signatoryPlayerId7}
                  onSelectCallback={onContactSignatory7Change}
                  sx={signatoryElementStyle}
                  error={
                    validateSignatorsStep &&
                    (signatoryPlayerId7 === 0 || Boolean(signatory7ErrorMessage))
                  }
                  disableQuickCreate
                  errorMessage={signatory7ErrorMessage}
                />
              )}
              {signatoryEntityType7 === candidateSignatorType && (
                <CandidatePicker
                  label="Signatory 7 Candidate"
                  value={signatoryPlayerId7}
                  onSelectCallback={onCandidateSignatory7Change}
                  sx={signatoryElementStyle}
                  error={
                    validateSignatorsStep &&
                    (signatoryPlayerId7 === 0 || Boolean(signatory7ErrorMessage))
                  }
                  helperText={signatory7ErrorMessage}
                />
              )}
              <Box display="flex" gap={1} alignItems="center">
                <Chip
                  label="[sign|signer7]"
                  size="small"
                  clickable
                  data-number="7"
                  onClick={copySignPlaceholder}
                  title="Copy to clipboard"
                  sx={chipStyle}
                />
                <Chip
                  label="[date|signer7]"
                  size="small"
                  clickable
                  data-number="7"
                  onClick={copyDatePlaceholder}
                  title="Copy to clipboard"
                  sx={chipStyle}
                />
              </Box>
            </Box>
            <Box display={numberOfSignatories >= 8 ? 'flex' : 'none'} gap={1}>
              <TextField
                select
                label="Signatory 8 Type"
                value={signatoryEntityType8.toString()}
                name="signatoryEntityType8"
                onChange={signatoryEntityTypeChangeCallback}
                sx={signatoryElementStyle}
                error={validateSignatorsStep && signatoryEntityType8 === 0}
              >
                {signatoryTypes}
              </TextField>
              {signatoryEntityType8 === userSignatorType && (
                <UserPicker
                  label="Signatory 8 User"
                  userId={signatoryPlayerId8}
                  onSelect={(u) => setSignatoryPlayerId8(u ? u.id : 0)}
                  sx={signatoryElementStyle}
                  error={validateSignatorsStep && signatoryPlayerId8 === 0}
                />
              )}
              {signatoryEntityType8 === contactSignatorType && (
                <ContactPicker
                  label="Signatory 8 Contact"
                  value={signatoryPlayerId8}
                  onSelectCallback={onContactSignatory8Change}
                  sx={signatoryElementStyle}
                  error={
                    validateSignatorsStep &&
                    (signatoryPlayerId8 === 0 || Boolean(signatory8ErrorMessage))
                  }
                  disableQuickCreate
                  errorMessage={signatory8ErrorMessage}
                />
              )}
              {signatoryEntityType8 === candidateSignatorType && (
                <CandidatePicker
                  label="Signatory 8 Candidate"
                  value={signatoryPlayerId8}
                  onSelectCallback={onCandidateSignatory8Change}
                  sx={signatoryElementStyle}
                  error={
                    validateSignatorsStep &&
                    (signatoryPlayerId8 === 0 || Boolean(signatory8ErrorMessage))
                  }
                  helperText={signatory8ErrorMessage}
                />
              )}
              <Box display="flex" gap={1} alignItems="center">
                <Chip
                  label="[sign|signer8]"
                  size="small"
                  clickable
                  data-number="8"
                  onClick={copySignPlaceholder}
                  title="Copy to clipboard"
                  sx={chipStyle}
                />
                <Chip
                  label="[date|signer8]"
                  size="small"
                  clickable
                  data-number="8"
                  onClick={copyDatePlaceholder}
                  title="Copy to clipboard"
                  sx={chipStyle}
                />
              </Box>
            </Box>
            <Box display={numberOfSignatories >= 9 ? 'flex' : 'none'} gap={1}>
              <TextField
                select
                label="Signatory 9 Type"
                value={signatoryEntityType9.toString()}
                name="signatoryEntityType9"
                onChange={signatoryEntityTypeChangeCallback}
                sx={signatoryElementStyle}
                error={validateSignatorsStep && signatoryEntityType9 === 0}
              >
                {signatoryTypes}
              </TextField>
              {signatoryEntityType9 === userSignatorType && (
                <UserPicker
                  label="Signatory 9 User"
                  userId={signatoryPlayerId9}
                  onSelect={(u) => setSignatoryPlayerId9(u ? u.id : 0)}
                  sx={signatoryElementStyle}
                  error={validateSignatorsStep && signatoryPlayerId9 === 0}
                />
              )}
              {signatoryEntityType9 === contactSignatorType && (
                <ContactPicker
                  label="Signatory 9 Contact"
                  value={signatoryPlayerId9}
                  onSelectCallback={onContactSignatory9Change}
                  sx={signatoryElementStyle}
                  error={
                    validateSignatorsStep &&
                    (signatoryPlayerId9 === 0 || Boolean(signatory9ErrorMessage))
                  }
                  disableQuickCreate
                  errorMessage={signatory9ErrorMessage}
                />
              )}
              {signatoryEntityType9 === candidateSignatorType && (
                <CandidatePicker
                  label="Signatory 9 Candidate"
                  value={signatoryPlayerId9}
                  onSelectCallback={onCandidateSignatory9Change}
                  sx={signatoryElementStyle}
                  error={
                    validateSignatorsStep &&
                    (signatoryPlayerId9 === 0 || Boolean(signatory9ErrorMessage))
                  }
                  helperText={signatory9ErrorMessage}
                />
              )}
              <Box display="flex" gap={1} alignItems="center">
                <Chip
                  label="[sign|signer9]"
                  size="small"
                  clickable
                  data-number="9"
                  onClick={copySignPlaceholder}
                  title="Copy to clipboard"
                  sx={chipStyle}
                />
                <Chip
                  label="[date|signer9]"
                  size="small"
                  clickable
                  data-number="9"
                  onClick={copyDatePlaceholder}
                  title="Copy to clipboard"
                  sx={chipStyle}
                />
              </Box>
            </Box>
            <Box display={numberOfSignatories >= 10 ? 'flex' : 'none'} gap={1}>
              <TextField
                select
                label="Signatory 10 Type"
                value={signatoryEntityType10.toString()}
                name="signatoryEntityType10"
                onChange={signatoryEntityTypeChangeCallback}
                sx={signatoryElementStyle}
                error={validateSignatorsStep && signatoryEntityType10 === 0}
              >
                {signatoryTypes}
              </TextField>
              {signatoryEntityType10 === userSignatorType && (
                <UserPicker
                  label="Signatory 10 User"
                  userId={signatoryPlayerId10}
                  onSelect={(u) => setSignatoryPlayerId10(u ? u.id : 0)}
                  sx={signatoryElementStyle}
                  error={validateSignatorsStep && signatoryPlayerId10 === 0}
                />
              )}
              {signatoryEntityType10 === contactSignatorType && (
                <ContactPicker
                  label="Signatory 10 Contact"
                  value={signatoryPlayerId10}
                  onSelectCallback={onContactSignatory10Change}
                  sx={signatoryElementStyle}
                  error={
                    validateSignatorsStep &&
                    (signatoryPlayerId10 === 0 || Boolean(signatory10ErrorMessage))
                  }
                  disableQuickCreate
                  errorMessage={signatory10ErrorMessage}
                />
              )}
              {signatoryEntityType10 === candidateSignatorType && (
                <CandidatePicker
                  label="Signatory 10 Candidate"
                  value={signatoryPlayerId10}
                  onSelectCallback={onCandidateSignatory10Change}
                  sx={signatoryElementStyle}
                  error={
                    validateSignatorsStep &&
                    (signatoryPlayerId10 === 0 || Boolean(signatory10ErrorMessage))
                  }
                  helperText={signatory10ErrorMessage}
                />
              )}
              <Box display="flex" gap={1} alignItems="center">
                <Chip
                  label="[sign|signer10]"
                  size="small"
                  clickable
                  data-number="10"
                  onClick={copySignPlaceholder}
                  title="Copy to clipboard"
                  sx={chipStyle}
                />
                <Chip
                  label="[date|signer10]"
                  size="small"
                  clickable
                  data-number="10"
                  onClick={copyDatePlaceholder}
                  title="Copy to clipboard"
                  sx={chipStyle}
                />
              </Box>
            </Box>
          </Stack>
          <Box sx={{ display: activeStep === 2 ? undefined : 'none' }}>
            <FilePicker
              singleFile
              files={filesToUpload}
              addFilesHandler={addFilesCallback}
              removeFileHandler={removeFileCallback}
              accept={filesFilter}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="error" onClick={closeHandler}>
            Cancel
          </Button>
          {activeStep > 0 && (
            <Button
              variant="contained"
              onClick={prevStepHandler}
              startIcon={<KeyboardArrowLeftIcon />}
            >
              Prev
            </Button>
          )}
          {!isActiveStepLast && (
            <Button
              variant="contained"
              onClick={nextStepHandler}
              endIcon={<KeyboardArrowRightIcon />}
            >
              Next
            </Button>
          )}
          {isActiveStepLast && (
            <Button
              variant="contained"
              color="success"
              disabled={filesToUpload.length !== 1}
              onClick={onSubmitCallback}
            >
              Ok
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
